import type { SvgIconComponent } from '@mui/icons-material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import EventNoteIcon from '@mui/icons-material/EventNote'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import CalculateIcon from '@mui/icons-material/Calculate'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CampaignIcon from '@mui/icons-material/Campaign'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import FolderIcon from '@mui/icons-material/Folder'
import HandshakeIcon from '@mui/icons-material/Handshake'
import PsychologyIcon from '@mui/icons-material/Psychology'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import HistoryIcon from '@mui/icons-material/History'
import ShieldIcon from '@mui/icons-material/Shield'
import PolicyIcon from '@mui/icons-material/Policy'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import NotificationsIcon from '@mui/icons-material/Notifications'
import GroupsIcon from '@mui/icons-material/Groups'
import LeaderboardIcon from '@mui/icons-material/Leaderboard'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import GppGoodIcon from '@mui/icons-material/GppGood'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { PAPEL_GESTOR_EQUIPE } from '@/types/usuario'
import { PAPEL_DPO } from '@/types/privacidade'

/**
 * Menu por persona (Onda 1 / A02) — o menu é DADO, não JSX espalhado.
 *
 * Uma única lista ordenada; `papeis` diz quem vê cada item. A ordem da lista É a
 * ordem do Figma para as duas personas (os itens exclusivos se intercalam sem
 * quebrar a ordem de nenhuma). OPERADOR/GERENTE usam o menu do Administrador com
 * os gates atuais: item sem permissão fica OCULTO (não desabilitado) — hoje isso
 * esconde "Usuários" e "Auditoria", que eram exclusivos de ADMIN.
 */

/**
 * `FINANCEIRO` (AN-PP5 §1.3) é papel de realm complementar, como o DPO; vira papel PRINCIPAL só
 * para quem não tem outro (usuário "só Financeiro"). Quem também é OPERADOR/GERENTE/ADMIN segue com
 * o papel principal dele e ganha os itens marcados com `tambemFinanceiro`.
 */
export type Papel = 'ADMIN' | 'GERENTE' | 'OPERADOR' | 'CONSULTOR' | 'CONSULTOR_ONBOARDING' | 'FINANCEIRO'

export interface ItemMenu {
  rotulo: string
  rota: string
  papeis: readonly Papel[]
  icone: SvgIconComponent
  /**
   * Rótulo para a persona consultor quando difere do back-office (mesma rota, mesmo item).
   * VC-09: "Central de Pendências" para o consultor; "Pendências e Documentos" no back-office.
   */
  rotuloConsultor?: string
  /**
   * AN-8 §4: o item também aparece para o GERENTE que tem o papel complementar
   * GESTOR_EQUIPE (o papel principal continua GERENTE; o menu lê as permissões).
   */
  tambemGestorEquipe?: boolean
  /**
   * AN-12 §7.4/§12: o item também aparece para quem tem o papel `DPO` (complementar, como o
   * GESTOR_EQUIPE) — qualquer que seja a persona, e mesmo sem papel principal.
   */
  tambemDpo?: boolean
  /**
   * AN-PP5 §1.3: o item também aparece para quem tem o papel complementar `FINANCEIRO` — nunca para a
   * persona consultor (CONSULTOR com FINANCEIRO residual continua consultor).
   */
  tambemFinanceiro?: boolean
}

const CONSULTORES: readonly Papel[] = ['CONSULTOR', 'CONSULTOR_ONBOARDING']
const BACKOFFICE: readonly Papel[] = ['ADMIN', 'GERENTE', 'OPERADOR']
const TODOS: readonly Papel[] = [...CONSULTORES, ...BACKOFFICE]

export const MENU: readonly ItemMenu[] = [
  { rotulo: 'Dashboard', rota: '/dashboard', papeis: TODOS, icone: DashboardIcon },
  { rotulo: 'Minhas Reservas', rota: '/reservas', papeis: CONSULTORES, icone: BookmarkAddedIcon },
  { rotulo: 'Reservas e Curadoria', rota: '/reservas-curadoria', papeis: BACKOFFICE, icone: FactCheckIcon },
  // 06/09: validação das reservas concluídas pelo consultor (dados + documentos) antes da
  // aprovação — tela própria, separada da curadoria.
  { rotulo: 'Validação de Reservas', rota: '/validacao-reservas', papeis: BACKOFFICE, icone: TaskAltIcon },
  { rotulo: 'Agenda', rota: '/agenda', papeis: TODOS, icone: EventNoteIcon },
  // 23/10: Estruturação e Operações e Clientes saíram do menu do consultor (pedido do
  // negócio); as rotas também barram o consultor (`router/SomenteBackoffice`).
  { rotulo: 'Estruturação', rota: '/estruturacao', papeis: BACKOFFICE, icone: AssessmentIcon },
  { rotulo: 'Pipeline Comercial', rota: '/pipeline', papeis: TODOS, icone: ViewWeekIcon },
  { rotulo: 'Operações e Clientes', rota: '/operacoes-clientes', papeis: BACKOFFICE, icone: SwapHorizIcon },
  // VC-09 (Onda 1 da Visão do Consultor): para o consultor a tela é a "Central de
  // Pendências"; o back-office mantém o nome do Figma. Rota única.
  {
    rotulo: 'Pendências e Documentos',
    rotuloConsultor: 'Central de Pendências',
    rota: '/pendencias-documentos',
    papeis: TODOS,
    icone: PendingActionsIcon,
  },
  { rotulo: 'Simulações', rota: '/simulacoes', papeis: TODOS, icone: CalculateIcon },
  // 28/09 (bloco 8, D2): spread, alíquota e modelo por produto são dado versionado com
  // vigência e origem, cadastrado pelo ADMIN — sem política vigente a simulação bloqueia.
  // Item fora dos 16 do Figma, logo abaixo de Simulações; backend ADMIN_ONLY.
  // P5 (AN-PP5 §1.3): o FINANCEIRO lê as políticas e publica spread/tributo (o backend confere).
  {
    rotulo: 'Políticas de simulação',
    rota: '/simulacoes/politicas',
    papeis: ['ADMIN', 'FINANCEIRO'],
    icone: CalculateIcon,
    tambemFinanceiro: true,
  },
  { rotulo: 'Financeiro', rota: '/financeiro', papeis: [...TODOS, 'FINANCEIRO'], icone: AccountBalanceWalletIcon },
  { rotulo: 'Campanhas', rota: '/campanhas', papeis: BACKOFFICE, icone: CampaignIcon },
  // AN-8 (Onda 4 da Visão do Consultor): equipes do gerente ao lado de Campanhas — ADMIN
  // mantém; o GERENTE com GESTOR_EQUIPE vê a própria em leitura.
  { rotulo: 'Equipes', rota: '/equipes', papeis: ['ADMIN'], icone: GroupsIcon, tambemGestorEquipe: true },
  // AN-10 (Onda 5 da Visão do Consultor): nível do consultor, só informativo (Q26) — ADMIN
  // cadastra e atribui; o GERENTE com GESTOR_EQUIPE vê o nível da própria equipe em leitura.
  // O consultor não tem o item: vê o próprio nível no dashboard e no Perfil.
  {
    rotulo: 'Níveis',
    rota: '/niveis-consultor',
    papeis: ['ADMIN'],
    icone: WorkspacePremiumIcon,
    tambemGestorEquipe: true,
  },
  // AN-8 §5: escopo do ranking (ADMIN edita, back-office lê) e lista por consultor (ADMIN e
  // GERENTE+GESTOR_EQUIPE; a página recorta).
  { rotulo: 'Ranking', rota: '/ranking', papeis: BACKOFFICE, icone: LeaderboardIcon },
  { rotulo: 'Produtos e Materiais', rota: '/produtos-materiais', papeis: CONSULTORES, icone: AccountBalanceIcon },
  { rotulo: 'Documentos e Materiais', rota: '/documentos-materiais', papeis: BACKOFFICE, icone: FolderIcon },
  // O catálogo de produtos é a MESMA página do consultor (`/produtos-materiais`), que já
  // traz "Novo produto", editar e remover para o ADMIN — só não tinha porta de entrada:
  // o item do Figma é exclusivo do consultor, e o back-office chegava ao cadastro
  // digitando a URL. Rótulo próprio porque, no menu do admin, "Produtos e Materiais" ao
  // lado de "Documentos e Materiais" seriam dois nomes quase iguais para coisas
  // diferentes (catálogo × conteúdos publicados). Ler o catálogo não é ADMIN-only no
  // backend; editar é, e a própria página esconde as ações de quem não pode.
  { rotulo: 'Catálogo de Produtos', rota: '/produtos-materiais', papeis: BACKOFFICE, icone: AccountBalanceIcon },
  // P6 (AN-PP6 §2.4/§8): fila de aprovação das políticas do produto — back-office (OPERADOR+ no backend), logo
  // abaixo do catálogo. Quem aprova cada componente é decidido pelo backend; a tela só explica.
  { rotulo: 'Políticas pendentes', rota: '/produtos/politicas-pendentes', papeis: BACKOFFICE, icone: FactCheckIcon },
  { rotulo: 'Parceiros', rota: '/parceiros', papeis: BACKOFFICE, icone: HandshakeIcon },
  { rotulo: 'Busca com IA', rota: '/ia/busca', papeis: TODOS, icone: PsychologyIcon },
  // Consultor: "apenas visualização do próprio acesso" (Figma 27:104).
  { rotulo: 'Usuários', rota: '/usuarios/meu-acesso', papeis: CONSULTORES, icone: ManageAccountsIcon },
  // Gate atual: gestão de usuários/acessos era exclusiva de ADMIN — segue oculta
  // para GERENTE/OPERADOR até decisão em contrário.
  { rotulo: 'Usuários', rota: '/usuarios-acessos', papeis: ['ADMIN'], icone: ManageAccountsIcon },
  // Tela de Consultores (docs/PLANO_TELA_CONSULTORES.md, Q1): só ADMIN, ao lado de Usuários.
  { rotulo: 'Consultores', rota: '/consultores', papeis: ['ADMIN'], icone: SupportAgentIcon },
  // Item do menu contratado do Figma (27:144). O backend de auditoria hoje é
  // ADMIN-only: a página atual nega com snackbar para o consultor; a versão
  // filtrada pelo próprio consultor chega na Onda 8 (A17). Mesmo tratamento de
  // "Estruturação" e "Simulações": menu contratado, negação decente até a onda.
  { rotulo: 'Auditoria', rota: '/auditoria', papeis: [...CONSULTORES, 'ADMIN'], icone: HistoryIcon },
  // Fila do DPO (Figma 30:254). Não está entre os 16 itens do menu do admin (3:3),
  // mas a tela não teria outro caminho além da URL; item extra só para ADMIN (gate
  // atual do backend, ADMIN_ONLY), logo após Auditoria. Decisão do coordenador na
  // integração do A13 — reversível quando o negócio disser onde a tela mora.
  { rotulo: 'Privacidade e Incidentes', rota: '/privacidade-incidentes', papeis: ['ADMIN'], icone: PolicyIcon },
  // AN-12 / L17 (Onda 5 da Visão do Consultor): retenção, retenção legal, fila de expurgo e
  // solicitações do titular. Back-office global de ADMIN e DPO (o DPO é papel complementar).
  { rotulo: 'Privacidade (LGPD)', rota: '/privacidade-lgpd', papeis: ['ADMIN'], icone: GppGoodIcon, tambemDpo: true },
  { rotulo: 'Meus Dados', rota: '/meus-dados', papeis: CONSULTORES, icone: ShieldIcon },
  { rotulo: 'Reportar Incidente', rota: '/reportar-incidente', papeis: CONSULTORES, icone: ReportProblemIcon },
  { rotulo: 'Notificações', rota: '/notificacoes', papeis: TODOS, icone: NotificationsIcon },
]

/**
 * Papel que decide qual persona de menu o usuário vê.
 *
 * Espelha o `resolverEscopo` (e o TenantScope do backend): CONSULTOR sem
 * ADMIN/GERENTE é consultor — OPERADOR residual junto NÃO promove ao menu de
 * back-office (defesa contra o defeito de defaultRoles). Sem papel conhecido → null.
 */
export const papelPrincipal = (permissoes: string[] | undefined | null): Papel | null => {
  const p = (permissoes ?? []).map((x) => x.toUpperCase())
  const consultor = p.includes('CONSULTOR')
  const adminOuGerente = p.includes('ADMIN') || p.includes('GERENTE')
  if (consultor && !adminOuGerente) return 'CONSULTOR'
  if (p.includes('ADMIN')) return 'ADMIN'
  if (p.includes('GERENTE')) return 'GERENTE'
  if (p.includes('OPERADOR')) return 'OPERADOR'
  if (p.includes('CONSULTOR_ONBOARDING')) return 'CONSULTOR_ONBOARDING'
  // Só Financeiro (sem outro papel conhecido): persona própria, não consultor nem back-office.
  if (p.includes('FINANCEIRO')) return 'FINANCEIRO'
  return null
}

/**
 * Tem o papel FINANCEIRO e NÃO é a persona consultor (AN-PP5 §1.3): o mesmo critério do backend
 * (`exigirBackoffice`: consultor com FINANCEIRO residual nunca entra).
 */
export const temPapelFinanceiro = (permissoes: string[] | undefined | null): boolean =>
  !ehConsultor(permissoes) && (permissoes ?? []).some((p) => p.toUpperCase() === 'FINANCEIRO')

/** Persona consultor (com ou sem onboarding), pelo mesmo critério do menu. */
export const ehConsultor = (permissoes: string[] | undefined | null): boolean => {
  const papel = papelPrincipal(permissoes)
  return papel === 'CONSULTOR' || papel === 'CONSULTOR_ONBOARDING'
}

/**
 * Itens visíveis para um papel, na ordem do Figma, já com o rótulo da persona.
 * `gestorEquipe` (AN-8): GERENTE com o papel complementar GESTOR_EQUIPE.
 */
export const itensPorPapel = (papel: Papel, gestorEquipe = false, dpo = false, financeiro = false): ItemMenu[] => {
  const consultor = papel === 'CONSULTOR' || papel === 'CONSULTOR_ONBOARDING'
  const gestor = gestorEquipe && papel === 'GERENTE'
  const fin = financeiro && !consultor
  return MENU.filter(
    (item) =>
      item.papeis.includes(papel) ||
      (gestor && item.tambemGestorEquipe) ||
      (dpo && item.tambemDpo) ||
      (fin && item.tambemFinanceiro)
  ).map((item) => (consultor && item.rotuloConsultor ? { ...item, rotulo: item.rotuloConsultor } : item))
}

/**
 * Itens visíveis para o usuário logado (via papel principal + GESTOR_EQUIPE + DPO).
 * DPO sem papel principal (só o papel de encarregado) vê apenas os itens do DPO.
 */
export const itensDoMenu = (permissoes: string[] | undefined | null): ItemMenu[] => {
  const papel = papelPrincipal(permissoes)
  const lista = (permissoes ?? []).map((p) => p.toUpperCase())
  const gestorEquipe = lista.includes(PAPEL_GESTOR_EQUIPE)
  const dpo = lista.includes(PAPEL_DPO)
  const financeiro = lista.includes('FINANCEIRO')
  if (papel) return itensPorPapel(papel, gestorEquipe, dpo, financeiro)
  return dpo ? MENU.filter((item) => item.tambemDpo) : []
}
